/**
 * Staff Constants
 *
 * Central source of truth for staff roles, statuses, and form defaults.
 */

import { SPECIALIZATION_CATALOGUE } from './specializations';

// ── Staff Roles ──────────────────────────────────────────────────────
export const STAFF_ROLES = {
  ADMIN: 'ADMIN',
  DOCTOR: 'DOCTOR',
  RECEPTIONIST: 'RECEPTIONIST',
  NURSE: 'NURSE',
  LAB_TECHNICIAN: 'LAB_TECHNICIAN',
  OTHER_STAFF: 'OTHER_STAFF',
  // F6 (IPD-MVP discharge-mis, BUILD-PLAN fast-slice-v2 §8 WAVE 2, §7.0
  // frozen contract) — mirrors backend `ClinicRole.ACCOUNTANT` verbatim.
  // Grants VIEW_MONTHLY_COLLECTIONS + EXPORT_MONTHLY_COLLECTIONS +
  // DOWNLOAD_BUSY_FILES + VIEW_IPD_REVENUE server-side (role_permissions.py);
  // nothing else.
  ACCOUNTANT: 'ACCOUNTANT',
};

/**
 * `?purpose=` of GET /providers/assignable. `emergency` = the org-wide roster for
 * a casualty admission (the provider wall does not apply; owner ruling R2).
 */
export const ASSIGNABLE_PURPOSE = {
  EMERGENCY: 'emergency',
};

export const STAFF_ROLE_LABELS = {
  [STAFF_ROLES.ADMIN]: 'Admin',
  [STAFF_ROLES.DOCTOR]: 'Doctor',
  [STAFF_ROLES.RECEPTIONIST]: 'Receptionist',
  [STAFF_ROLES.NURSE]: 'Nurse',
  [STAFF_ROLES.LAB_TECHNICIAN]: 'Lab Technician',
  [STAFF_ROLES.OTHER_STAFF]: 'Other Staff',
  [STAFF_ROLES.ACCOUNTANT]: 'Accountant',
};

export const STAFF_ROLE_OPTIONS = Object.entries(STAFF_ROLE_LABELS).map(
  ([value, label]) => ({ value, label })
);

// bk-444 — PHARMACY is a real role, derived from the login group
// (PermissionContext.js `deriveRoleFromSession`), but it is NOT creatable
// from the staff screen — so it must NOT be added to STAFF_ROLES /
// STAFF_ROLE_LABELS (STAFF_ROLE_OPTIONS is derived from that map and would
// make PHARMACY selectable there, which is wrong). This greeting-only map
// extends the shared one so AdminDashboard's greeting has a real label
// instead of falling back to "User" once pharmacy users exist.
export const GREETING_ROLE_LABELS = {
  ...STAFF_ROLE_LABELS,
  PHARMACY: 'Pharmacist',
};

// ── Doctor Specializations ────────────────────────────────────────────
// Derived from the ONE catalogue (specializations.js) — never a second list.
export const SPECIALIZATION_OPTIONS = SPECIALIZATION_CATALOGUE.map(
  ({ value, label }) => ({ value, label })
);

export const SPECIALIZATION_LABELS = SPECIALIZATION_OPTIONS.reduce(
  (acc, opt) => ({ ...acc, [opt.value]: opt.label }),
  {}
);

export const isKnownSpecialisation = (value) =>
  Object.prototype.hasOwnProperty.call(SPECIALIZATION_LABELS, value);

/**
 * Typed text -> stored value: a case-insensitive match to a code or a label
 * gives the code; anything else is "Other" and kept as the trimmed text.
 */
export function normaliseSpecialisation(text) {
  const t = String(text ?? '').trim();
  if (!t) return '';
  const lower = t.toLowerCase();
  const hit = SPECIALIZATION_OPTIONS.find(
    (o) => o.value.toLowerCase() === lower || o.label.toLowerCase() === lower
  );
  return hit ? hit.value : t;
}

/**
 * The speciality's NAME, for running text (PO ruling, D42): the dropdown label
 * with everything from the first `(` dropped, trimmed — "Cardiologist (Heart
 * Specialist)" → "Cardiologist", "M.S ENT (Ear, Nose, Throat)" → "M.S ENT". A
 * `/` is part of the name, not a split point: "Gynecologist / Obstetrician"
 * stays whole.
 *
 * Derived from `SPECIALIZATION_LABELS` — the same single map every screen
 * reads — so there is no second list of names to keep in step.
 *
 * @returns {string|null} null when there is no value or no label for it, which
 *   is how a caller knows to render NOTHING rather than "undefined symptoms" or
 *   a raw stored value like "MS_ENT symptoms".
 */
export function specialisationName(value) {
  const label = SPECIALIZATION_LABELS[value];
  if (!label) return null;
  const name = label.split('(')[0].trim();
  return name || null;
}

// ── Staff Status ─────────────────────────────────────────────────────
export const STAFF_STATUS = {
  ACTIVE: 'ACTIVE',
  DISABLED: 'DISABLED',
  INACTIVE: 'INACTIVE',
  PENDING: 'PENDING',
};

export const STAFF_STATUS_LABELS = {
  [STAFF_STATUS.ACTIVE]: 'Active',
  [STAFF_STATUS.DISABLED]: 'Disabled',
  [STAFF_STATUS.INACTIVE]: 'Inactive',
  [STAFF_STATUS.PENDING]: 'Pending',
};

// ── Empty staff row template ─────────────────────────────────────────
export const EMPTY_STAFF_ROW = {
  first_name: '',
  last_name: '',
  mobile: '',
  email: '',
  role: STAFF_ROLES.DOCTOR,
  specialization: '',
  registration_no: '',
  registration_number: '',
  registration_authority: '',
  role_name: '',
  department_id: '',
};

/**
 * Create a fresh staff row with a unique local key.
 * @returns {Object}
 */
export const createEmptyStaffRow = () => ({
  ...EMPTY_STAFF_ROW,
  _key: `staff_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
});
